import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { useGlobalContext } from "../../context/global";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  MenuItem,
  Paper,
  Popover,
  Select,
  Skeleton,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import HistoryIcon from "@mui/icons-material/History";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import axios from "axios";
import { toast } from "react-toastify";
import { HEADING_TITLE_COLOR, RULE_TYPE } from "./lib/constants";
import {
  BID_V2_COLORS,
  sectionCardSx,
  sectionSubtitleSx,
  tableHeadCellSx,
} from "./lib/bidAutomationStyles";
import {
  formatValidationIssueMessage,
  getCampaignCount,
  getModeLabel,
  getRuleCampaignIds,
  getRuleId,
  getRuleLastRunTime,
  getRuleName,
  getRuleUpdatedAt,
  getScheduleRange,
} from "./lib/helpers";
import { fetchCampaignScheduleConflictValidation } from "./lib/validation";
import useUserDetails from "../../helper/userData/useUserDetails";
import BidAutomationLogsModal from "./components/BidAutomationLogsModal";
import CustomPagination from "../../components/CustomPagination/CustomPagination";

const SHORT_MONTHS = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

function parseScheduleDateString(value) {
  const s = String(value ?? "").trim();
  if (!s) return null;
  const isoDay = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (isoDay) {
    const y = parseInt(isoDay[1], 10);
    const m = parseInt(isoDay[2], 10) - 1;
    const d = parseInt(isoDay[3], 10);
    const dt = new Date(y, m, d);
    return Number.isNaN(dt.getTime()) ? null : dt;
  }
  const dt = new Date(s);
  return Number.isNaN(dt.getTime()) ? null : dt;
}

function formatScheduleDateForList(value) {
  const dt = parseScheduleDateString(value);
  if (!dt) return null;
  const mon = SHORT_MONTHS[dt.getMonth()];
  return `${dt.getDate()} ${mon} ${dt.getFullYear()}`;
}

function formatCompactDateTime(value) {
  const dt = new Date(value);
  if (Number.isNaN(dt.getTime())) return null;
  const time = dt.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
  return `${dt.getDate()} ${SHORT_MONTHS[dt.getMonth()]} ${dt.getFullYear()}, ${time}`;
}

function ScheduleMetaRow({ label, value }) {
  const text = value || "-";
  return (
    <Stack direction="row" spacing={0.75} alignItems="baseline">
      <Typography
        sx={{
          width: 68,
          flexShrink: 0,
          fontSize: "0.625rem",
          fontWeight: 700,
          letterSpacing: "0.04em",
          textTransform: "uppercase",
          color: BID_V2_COLORS.muted,
        }}
      >
        {label}
      </Typography>
      <Tooltip title={text}>
        <Typography
          noWrap
          sx={{ fontSize: "0.75rem", color: BID_V2_COLORS.body, maxWidth: 168 }}
        >
          {text}
        </Typography>
      </Tooltip>
    </Stack>
  );
}

const SKELETON_ROW_COUNT = 6;
const LIST_COLUMNS = [
  "Status",
  "Rule Name",
  "Mode",
  "Campaigns",
  "Updated At",
  "Last Run Time",
  "Schedule Config",
  "Modified By",
  "Action",
];
const PAGE_SIZE_OPTIONS = [50, 100, 200, 300, 500];

const stickyActionCellSx = {
  position: "sticky",
  right: 0,
  zIndex: 2,
  bgcolor: "#fff",
  boxShadow: "-6px 0 8px rgba(15, 23, 42, 0.04)",
  "tr:hover &": { bgcolor: "#F8F7FC" },
};

const stickyActionHeadSx = {
  ...stickyActionCellSx,
  zIndex: 3,
  bgcolor: BID_V2_COLORS.tableHeadBg,
  "tr:hover &": { bgcolor: BID_V2_COLORS.tableHeadBg },
};

function BidAutomationList() {
  const navigate = useNavigate();
  const dashboardSideBarVisibiltyState = useSelector(
    (state) => state.dashboardData.dashboardSideBarVisibiltyState
  );
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageLimit, setPageLimit] = useState(50);
  const [totalCount, setTotalCount] = useState(0);
  const [togglingId, setTogglingId] = useState(null);
  const [logsModalRuleName, setLogsModalRuleName] = useState(null);
  const [triggerConfirmRule, setTriggerConfirmRule] = useState(null);
  const [triggeringId, setTriggeringId] = useState(null);
  const [activatePopover, setActivatePopover] = useState({
    anchorEl: null,
    rule: null,
  });
  const [activateValidation, setActivateValidation] = useState({
    loading: false,
    valid: false,
    issues: [],
  });

  const { clientId, selectedPlatform } = useGlobalContext();
  const userPlatform = selectedPlatform ?? "amazon";
  const [userDetails] = useUserDetails();
  const email = userDetails?.user?.email ?? "";

  const loadRules = useCallback(async () => {
    if (!clientId) {
      setRules([]);
      setTotalCount(0);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.get(
        `${process.env.REACT_APP_ADS_API}/client/${clientId}/automation-rule-set?source=${userPlatform}&type=${RULE_TYPE}&order_by=updated_at&sorting_order=desc&page=${currentPage - 1}&limit=${pageLimit}`
      );
      const list = Array.isArray(data) ? data : Array.isArray(data?.data) ? data.data : [];
      const totalFromApi = data?.total_count ?? null;
      setRules(list.filter((rule) => rule?.type === RULE_TYPE));
      if (totalFromApi !== null && totalFromApi !== undefined) {
        setTotalCount(totalFromApi);
      } else if (list.length < pageLimit) {
        setTotalCount((currentPage - 1) * pageLimit + list.length);
      } else {
        setTotalCount((currentPage - 1) * pageLimit + list.length + 1);
      }
    } catch (error) {
      toast.error(
        error?.response?.data?.message ??
          error?.response?.data?.error ??
          error?.message ??
          "Failed to load Bid Automation rules"
      );
      setRules([]);
    } finally {
      setLoading(false);
    }
  }, [clientId, userPlatform, currentPage, pageLimit]);

  const handleConfirmTrigger = async () => {
    const rule = triggerConfirmRule;
    const ruleId = getRuleId(rule);
    if (!ruleId || !clientId) return;
    setTriggeringId(ruleId);
    try {
      const params = new URLSearchParams({
        type: RULE_TYPE,
        client_id: clientId,
        TRIGGER_BY: email,
      });
      await axios.post(
        `${process.env.REACT_APP_ADS_API}/client/${clientId}/automation_rule_set/${ruleId}:execute-automation-rule-set?${params.toString()}`
      );
      toast.success(`"${getRuleName(rule)}" was triggered.`);
      setTriggerConfirmRule(null);
      loadRules();
    } catch (error) {
      toast.error(
        error?.response?.data?.message ??
          error?.response?.data?.error ??
          error?.message ??
          "Some error occurred while executing the rule"
      );
    } finally {
      setTriggeringId(null);
    }
  };

  useEffect(() => {
    loadRules();
  }, [loadRules]);

  useEffect(() => {
    setCurrentPage(1);
  }, [clientId]);

  const getFrequencyText = (rule) => {
    const values = rule?.schedule_config?.frequency_values;
    if (!Array.isArray(values) || values.length === 0 || values.length >= 7) {
      return "All Days";
    }
    return values
      .map((day) => {
        const name = String(day ?? "");
        return name ? name.charAt(0).toUpperCase() + name.slice(1) : "";
      })
      .filter(Boolean)
      .join(", ");
  };

  const getLookbackText = (rule) => {
    const labels = {
      yesterday: "Last 1 day",
      LAST_1_DAY: "Last 1 day",
      LAST_3_DAY: "Last 3 days",
      LAST_7_DAY: "Last 7 days",
      LAST_14_DAY: "Last 14 days",
    };
    const periods = [
      ...new Set(
        (rule?.rule_tasks ?? [])
          .map((task) => task?.entity_metric_filter?.time_period)
          .filter(Boolean)
      ),
    ];
    if (!periods.length) return "-";
    return periods.map((period) => labels[period] ?? period).join(", ");
  };

  const getDateRangeText = (rule) => {
    const { start, end } = getScheduleRange(rule);
    if (!start && !end) return "No date range";
    if (!start || !end) return start || end || "-";
    const a = formatScheduleDateForList(start);
    const b = formatScheduleDateForList(end);
    if (!a || !b) return `${start} to ${end}`;
    return `${a} to ${b}`;
  };

  const handleStatusSwitch = (event, rule) => {
    if (rule.status === "Active") {
      patchRuleStatus(rule, "Paused");
      return;
    }
    const anchorEl = event.currentTarget.closest("[data-bid-status-anchor]") ?? event.currentTarget;
    setActivatePopover({ anchorEl, rule });
  };

  const closeActivatePopover = useCallback(() => {
    setActivatePopover({ anchorEl: null, rule: null });
    setActivateValidation({ loading: false, valid: false, issues: [] });
  }, []);

  const patchRuleStatus = useCallback(
    async (rule, nextStatus) => {
      const ruleId = getRuleId(rule);
      if (!ruleId || !clientId) return false;
      setTogglingId(ruleId);
      try {
        const { data: res } = await axios.patch(
          `${process.env.REACT_APP_ADS_API}/client/${clientId}/automation-rule-set/${ruleId}?source=${userPlatform}&type=${RULE_TYPE}`,
          { status: nextStatus }
        );
        if (res?.success === false) {
          throw new Error("Status update failed");
        }
        toast.success(`"${getRuleName(rule)}" is now ${nextStatus}.`);
        await loadRules();
        return true;
      } catch (error) {
        toast.error(
          error?.response?.data?.message ??
            error?.response?.data?.error ??
            error?.message ??
            "Failed to update rule status"
        );
        return false;
      } finally {
        setTogglingId(null);
      }
    },
    [clientId, userPlatform, loadRules]
  );

  const runActivateValidation = useCallback(
    async (rule) => {
      const { start, end } = getScheduleRange(rule);
      setActivateValidation({ loading: true, valid: false, issues: [] });
      const result = await fetchCampaignScheduleConflictValidation({
        clientId,
        userPlatform,
        campaignIds: getRuleCampaignIds(rule),
        scheduleStartDate: start,
        scheduleEndDate: end,
        excludeRuleSetId: getRuleId(rule),
      });
      setActivateValidation({
        loading: false,
        valid: result.valid,
        issues: result.issues,
      });
    },
    [clientId, userPlatform]
  );

  useEffect(() => {
    if (!activatePopover.rule) return undefined;
    runActivateValidation(activatePopover.rule);
    return undefined;
  }, [activatePopover.rule, runActivateValidation]);

  const handleConfirmActivate = async () => {
    const rule = activatePopover.rule;
    if (!rule || activateValidation.loading || !activateValidation.valid) return;
    closeActivatePopover();
    await patchRuleStatus(rule, "Active");
  };

  const activateRuleName = activatePopover.rule ? getRuleName(activatePopover.rule) : "";

  const sortedRules = useMemo(
    () =>
      [...rules].sort(
        (a, b) =>
          new Date(getRuleUpdatedAt(b) || 0).getTime() -
          new Date(getRuleUpdatedAt(a) || 0).getTime()
      ),
    [rules]
  );

  const totalPages = Math.max(1, Math.ceil(totalCount / pageLimit));
  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };
  const handlePageLimitChange = (event) => {
    setPageLimit(Number(event.target.value));
    setCurrentPage(1);
  };

  return (
    <div
      className={
        dashboardSideBarVisibiltyState
          ? "main-content-container-onopen-mode"
          : "main-content-container-oncolapse-mode"
      }
      id="main-box"
    >
      <Box sx={{ py: 0, px: { xs: 2, md: 3 }, minHeight: "100%" }}>
        <Box sx={{ maxWidth: 1360, mx: "auto" }}>
          <Box sx={{ py: 0, mb: 4 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Box>
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 700,
                    color: HEADING_TITLE_COLOR,
                    fontSize: "1.375rem",
                    mb: 0.5,
                  }}
                >
                  Bid Automation Rules
                </Typography>
                <Typography variant="body2" sx={{ ...sectionSubtitleSx }}>
                  Automated bid optimization by cases, match types, and schedule.
                </Typography>
              </Box>
              <Button
                size="small"
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => navigate("/bid-automation-list/create")}
                disabled={!clientId}
              >
                Create Rule
              </Button>
            </Stack>
          </Box>

          {!clientId ? (
            <Typography color="text.secondary">Select a client to load Bid Automation rules.</Typography>
          ) : (
          <TableContainer
            component={Paper}
            variant="outlined"
            sx={{ ...sectionCardSx, overflow: "auto" }}
          >
            <Table size="small" sx={{ minWidth: 1080 }}>
              <TableHead>
                <TableRow>
                  {LIST_COLUMNS.map((label) => (
                    <TableCell
                      key={label}
                      align="center"
                      sx={label === "Action" ? { ...tableHeadCellSx, ...stickyActionHeadSx } : tableHeadCellSx}
                    >
                      {label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  Array.from({ length: SKELETON_ROW_COUNT }, (_, i) => (
                    <TableRow key={`sk-${i}`}>
                      {Array.from({ length: LIST_COLUMNS.length }).map((__, j) => (
                        <TableCell key={j} align="center">
                          <Skeleton
                            variant="text"
                            width={j === LIST_COLUMNS.length - 1 ? 36 : "80%"}
                            height={22}
                            sx={{ mx: "auto" }}
                          />
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
                ) : sortedRules.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={LIST_COLUMNS.length}
                      align="center"
                      sx={{ height: 100 }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                          height: "100%",
                        }}
                      >
                        <Typography color="text.secondary">
                          No Bid Automation rules found. Click Create Rule to add one.
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                ) : (
                  sortedRules.map((rule) => {
                    const oid = getRuleId(rule);
                    return (
                      <TableRow key={oid} hover>
                        <TableCell align="center">
                          <Stack
                            data-bid-status-anchor
                            direction="row"
                            alignItems="center"
                            justifyContent="center"
                            sx={{ minHeight: 38, minWidth: 58 }}
                          >
                            {togglingId === oid ? (
                              <CircularProgress size={22} thickness={5} />
                            ) : (
                              <Switch
                                size="small"
                                checked={rule.status === "Active"}
                                disabled={!oid}
                                onChange={(event) => handleStatusSwitch(event, rule)}
                              />
                            )}
                          </Stack>
                        </TableCell>
                        <TableCell align="left" sx={{ maxWidth: 240 }}>
                          <Tooltip title={getRuleName(rule)}>
                            <Typography
                              sx={{
                                fontWeight: 600,
                                color: BID_V2_COLORS.title,
                                fontSize: "0.8125rem",
                                display: "-webkit-box",
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: "vertical",
                                overflow: "hidden",
                              }}
                            >
                              {getRuleName(rule)}
                            </Typography>
                          </Tooltip>
                        </TableCell>
                        <TableCell align="center">
                          <Typography
                            component="span"
                            sx={{
                              display: "inline-block",
                              px: 1,
                              py: 0.25,
                              borderRadius: 10,
                              fontSize: "0.6875rem",
                              fontWeight: 700,
                              lineHeight: 1.6,
                              color: BID_V2_COLORS.title,
                              bgcolor: BID_V2_COLORS.tableHeadBg,
                              whiteSpace: "nowrap",
                            }}
                          >
                            {getModeLabel(rule)}
                          </Typography>
                        </TableCell>
                        <TableCell
                          align="center"
                          sx={{ fontSize: "0.875rem", color: BID_V2_COLORS.body }}
                        >
                          {getCampaignCount(rule)}
                        </TableCell>
                        <TableCell
                          align="center"
                          sx={{ fontSize: "0.75rem", color: BID_V2_COLORS.body, whiteSpace: "nowrap" }}
                        >
                          {getRuleUpdatedAt(rule) ? formatCompactDateTime(getRuleUpdatedAt(rule)) : "-"}
                        </TableCell>
                        <TableCell
                          align="center"
                          sx={{ fontSize: "0.75rem", color: BID_V2_COLORS.body, whiteSpace: "nowrap" }}
                        >
                          {getRuleLastRunTime(rule) ? formatCompactDateTime(getRuleLastRunTime(rule)) : "-"}
                        </TableCell>
                        <TableCell align="left" sx={{ py: 1.25 }}>
                          <Stack spacing={0.4}>
                            <ScheduleMetaRow label="Dates" value={getDateRangeText(rule)} />
                            <ScheduleMetaRow label="Frequency" value={getFrequencyText(rule)} />
                            <ScheduleMetaRow label="Lookback" value={getLookbackText(rule)} />
                          </Stack>
                        </TableCell>
                        <TableCell
                          align="center"
                          sx={{ fontSize: "0.75rem", color: BID_V2_COLORS.body, whiteSpace: "nowrap" }}
                        >
                          {rule?.modified_by || "-"}
                        </TableCell>
                        <TableCell align="center" sx={stickyActionCellSx}>
                          <Stack direction="row" justifyContent="center" spacing={0.5}>
                            <Tooltip title="Run now">
                              <span>
                                <IconButton
                                  disabled={!oid || triggeringId === oid}
                                  onClick={() => setTriggerConfirmRule(rule)}
                                  size="small"
                                  sx={{ color: BID_V2_COLORS.muted }}
                                >
                                  {triggeringId === oid ? (
                                    <CircularProgress size={16} thickness={5} />
                                  ) : (
                                    <PlayCircleOutlineIcon fontSize="small" />
                                  )}
                                </IconButton>
                              </span>
                            </Tooltip>
                            <Tooltip title="View logs">
                              <span>
                                <IconButton
                                  disabled={!getRuleName(rule)}
                                  onClick={() => setLogsModalRuleName(getRuleName(rule))}
                                  size="small"
                                  sx={{ color: BID_V2_COLORS.muted }}
                                >
                                  <HistoryIcon fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                            <Tooltip title="Edit rule">
                              <span>
                                <IconButton
                                  disabled={!oid}
                                  onClick={() =>
                                    navigate(`/bid-automation-list/create?id=${oid}`)
                                  }
                                  color="primary"
                                  size="small"
                                >
                                  <EditIcon fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
          )}

          {!loading && (totalCount > 0 || currentPage > 1) && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 1,
                py: 2,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  color: "#4f5366",
                }}
              >
                <Typography variant="body2" sx={{ fontSize: "12px" }}>
                  Page Size
                </Typography>
                <FormControl size="small" sx={{ minWidth: 80 }}>
                  <Select
                    value={pageLimit}
                    onChange={handlePageLimitChange}
                    disabled={loading}
                    sx={{ height: 36, fontSize: "12px", color: "#333333" }}
                    MenuProps={{ disableScrollLock: true }}
                  >
                    {PAGE_SIZE_OPTIONS.map((option) => (
                      <MenuItem key={option} value={option} sx={{ fontSize: "12px" }}>
                        {option}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>

              <CustomPagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={handlePageChange}
                lazyLoading={loading}
                totalItems={totalCount}
                itemsPerPage={pageLimit}
                hidePagination={!totalCount}
              />
            </Box>
          )}
        </Box>
      </Box>

      <BidAutomationLogsModal
        open={Boolean(logsModalRuleName)}
        onClose={() => setLogsModalRuleName(null)}
        ruleName={logsModalRuleName}
        clientId={clientId}
        source={userPlatform}
      />

      <Popover
        open={Boolean(activatePopover.anchorEl)}
        anchorEl={activatePopover.anchorEl}
        onClose={closeActivatePopover}
        disableRestoreFocus
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        transformOrigin={{ vertical: "top", horizontal: "center" }}
        slotProps={{
          paper: {
            sx: {
              p: 2,
              width: 360,
              maxWidth: "calc(100vw - 32px)",
              borderRadius: 2,
              border: `1px solid ${BID_V2_COLORS.border}`,
              boxShadow: "0 8px 24px rgba(15, 23, 42, 0.12)",
            },
          },
        }}
      >
        <Typography sx={{ fontWeight: 700, fontSize: "0.9375rem", color: BID_V2_COLORS.title, mb: 0.5 }}>
          Activate rule?
        </Typography>
        <Typography sx={{ fontSize: "0.8125rem", color: BID_V2_COLORS.muted, mb: 1.5 }}>
          {activateRuleName}
        </Typography>
        {activateValidation.loading ? (
          <Stack direction="row" alignItems="center" spacing={1} sx={{ py: 1 }}>
            <CircularProgress size={20} />
            <Typography sx={{ fontSize: "0.8125rem", color: BID_V2_COLORS.muted }}>
              Validating schedule…
            </Typography>
          </Stack>
        ) : null}
        {!activateValidation.loading && activateValidation.issues.length > 0 ? (
          <Box role="alert" sx={{ mb: 1.5 }}>
            {activateValidation.issues.map((issue, index) => (
              <Typography
                key={`activate-issue-${index}`}
                sx={{ fontSize: "0.8125rem", fontWeight: 500, color: "error.main", lineHeight: 1.5 }}
              >
                {formatValidationIssueMessage(issue?.message)}
              </Typography>
            ))}
          </Box>
        ) : null}
        {!activateValidation.loading && activateValidation.valid ? (
          <Typography sx={{ fontSize: "0.8125rem", color: BID_V2_COLORS.body, mb: 1.5 }}>
            No schedule conflicts found. You can activate this rule.
          </Typography>
        ) : null}
        <Stack direction="row" justifyContent="flex-end" spacing={1}>
          <Button size="small" variant="outlined" onClick={closeActivatePopover} sx={{ textTransform: "none", fontWeight: 600 }}>
            Cancel
          </Button>
          <Button
            size="small"
            variant="contained"
            onClick={handleConfirmActivate}
            disabled={
              activateValidation.loading ||
              !activateValidation.valid ||
              togglingId === getRuleId(activatePopover.rule)
            }
            sx={{ textTransform: "none", fontWeight: 600, boxShadow: "none" }}
          >
            Activate
          </Button>
        </Stack>
      </Popover>

      <Dialog
        open={Boolean(triggerConfirmRule)}
        onClose={() => {
          if (!triggeringId) setTriggerConfirmRule(null);
        }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 700, color: BID_V2_COLORS.title }}>
          Run this rule now?
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: "0.875rem", color: BID_V2_COLORS.body }}>
            {triggerConfirmRule ? getRuleName(triggerConfirmRule) : ""} will be triggered
            manually.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, pt: 0 }}>
          <Button
            size="small"
            variant="outlined"
            disabled={Boolean(triggeringId)}
            onClick={() => setTriggerConfirmRule(null)}
            sx={{ textTransform: "none", fontWeight: 600 }}
          >
            Cancel
          </Button>
          <Button
            size="small"
            variant="contained"
            disabled={Boolean(triggeringId) || !triggerConfirmRule}
            onClick={handleConfirmTrigger}
            sx={{ textTransform: "none", fontWeight: 600, boxShadow: "none" }}
          >
            {triggeringId ? "Running…" : "Run"}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}

export default BidAutomationList;
